import { Injectable, NgZone, Renderer2, RendererFactory2, inject } from '@angular/core';
import {ThemeId} from '../enums/theme-id';
import {SettingsService} from '../../canvas/services/settings.service';
import {GoogleAnalyticsService} from './google-analytics.service';
import {APP_THEMES, AppTheme, DEFAULT_APP_THEME, resolveAppTheme} from '../themes/app-themes';

/** What the reader picked: a theme, or "whatever this device is set to". */
export type ThemeChoice = ThemeId | 'auto';

/** Where the choice is kept. `theme` itself always holds the theme in use. */
const MODE_KEY = 'themeMode';

/** Where the colour theme is kept (Classic, Dusk...), beside the mode. */
const APP_THEME_KEY = 'appTheme';

@Injectable({
  providedIn: 'root'
})

export class ThemeService {
  private settings = inject(SettingsService);
  private analytics = inject(GoogleAnalyticsService);
  private ngZone = inject(NgZone);

  private renderer: Renderer2;
  /** The choice in force; read from storage once, not on every change-detection pass. */
  private currentMode: ThemeChoice | null = null;
  private systemQuery: MediaQueryList | null = null;
  private readonly onSystemChange = () => this.followSystem();
  /** The colour theme in force (an id from APP_THEMES). */
  private currentAppTheme = DEFAULT_APP_THEME;

  /** The colour themes on offer, for the pickers. */
  readonly appThemes: readonly AppTheme[] = APP_THEMES;

  constructor() {
    const rendererFactory = inject(RendererFactory2);

    this.renderer = rendererFactory.createRenderer(null, null);

    // An embed can ask for a theme in its URL (`?theme=light|dark|auto`) so the
    // widget matches the site hosting it. That choice belongs to the embed and
    // not to this browser, so it is applied without touching what the visitor
    // picked for their own account.
    const embedTheme = ThemeService.themeFromUrl();
    if (embedTheme) {
      this.applyTheme(embedTheme);
    } else if (this.mode === 'auto') {
      this.currentMode = 'auto';
      this.watchSystem(true);
      this.followSystem();
    } else {
      this.setTheme(this.settings.getLocalValue('theme', ThemeId.LIGHT));
    }

    // The colour theme is the visitor's own, like the mode, so an embed on
    // someone else's site does not take it: it shows the default unless its
    // URL names one (`?appTheme=dusk`).
    const embedded = ThemeService.isEmbedded();
    const urlTheme = ThemeService.appThemeFromUrl();
    this.applyAppTheme(resolveAppTheme(urlTheme
      ?? (embedded ? DEFAULT_APP_THEME : this.settings.getLocalValue(APP_THEME_KEY, DEFAULT_APP_THEME))));
  }

  /** The colour theme in force: an id from {@link APP_THEMES}. */
  get appTheme(): string {
    return this.currentAppTheme;
  }

  /** Switches the colour theme at once, for this browser, and remembers it. */
  setAppTheme(id: string) {
    const theme = resolveAppTheme(id);
    if (theme === this.currentAppTheme) {
      return;
    }
    this.settings.setLocalValue(APP_THEME_KEY, theme);
    this.applyAppTheme(theme);
    this.analytics.logEvent(`settings_theme_${theme}`, 'app_theme');
  }

  /**
   * Light, dark, or 'auto' (the device's setting, followed as it changes).
   *
   * The theme in use is always written to `theme` as light or dark, so the
   * map tiles and everything else that reads it work the same under 'auto';
   * the choice itself is kept beside it, under `themeMode`.
   */
  setTheme(choice: ThemeChoice) {
    const prevMode = this.mode;
    this.currentMode = choice;
    this.settings.setLocalValue(MODE_KEY, choice);
    this.watchSystem(choice === 'auto');
    const theme = choice === 'auto' ? ThemeService.systemTheme() : choice;
    if (this.settings.getLocalValue('theme', ThemeId.LIGHT) !== theme) {
      this.settings.setLocalValue('theme', theme);
    }
    this.applyTheme(theme);

    if (choice !== prevMode) {
      this.analytics.logEvent(`settings_${choice}_mode`, `${choice}_mode`);
    }
  }

  /** Dark is showing now: chosen, or the device's under 'auto'. */
  get isDark(): boolean {
    return this.settings.getLocalValue('theme', ThemeId.LIGHT) === ThemeId.DARK;
  }

  /**
   * The menu's sun/moon button: to the other mode of what shows now. Given
   * where the button is, the new mode spreads out from it as a circle over
   * the old one (a view transition); under reduced motion the two cross-fade,
   * and where the browser has no view transitions it simply switches.
   */
  toggleMode(origin?: {x: number, y: number}) {
    const next = this.isDark ? ThemeId.LIGHT : ThemeId.DARK;
    // In the zone, so the button's own icon is redrawn for the new snapshot.
    const update = () => this.ngZone.run(() => this.setTheme(next));
    const doc = document as Document & {
      startViewTransition?: (update: () => void) => {ready: Promise<void>, finished: Promise<void>}
    };
    if (!origin || !doc.startViewTransition) {
      update();
      return;
    }
    if (ThemeService.reducedMotion()) {
      doc.startViewTransition(update);
      return;
    }
    const root = document.documentElement;
    root.classList.add('mm-mode-reveal');
    const transition = doc.startViewTransition(update);
    transition.ready.then(() => {
      const {x, y} = origin;
      const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
      // Longer than the motion tokens on purpose: the whole screen changes,
      // and the circle has the full diagonal to cover.
      root.animate(
        {clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`]},
        {duration: 450, easing: 'cubic-bezier(0.2, 0, 0.2, 1)', pseudoElement: '::view-transition-new(root)'});
    }).catch(() => root.classList.remove('mm-mode-reveal'));
    transition.finished.finally(() => root.classList.remove('mm-mode-reveal'));
  }

  private static reducedMotion(): boolean {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /** The reader's choice; a browser from before 'auto' existed has only `theme`. */
  get mode(): ThemeChoice {
    if (this.currentMode) {
      return this.currentMode;
    }
    const mode = this.settings.getLocalValue(MODE_KEY, null);
    if (mode === 'auto' || mode === ThemeId.LIGHT || mode === ThemeId.DARK) {
      return mode;
    }
    return this.settings.getLocalValue('theme', ThemeId.LIGHT) === ThemeId.DARK ? ThemeId.DARK : ThemeId.LIGHT;
  }

  private followSystem() {
    const theme = ThemeService.systemTheme();
    if (this.settings.getLocalValue('theme', ThemeId.LIGHT) !== theme) {
      this.settings.setLocalValue('theme', theme);
    }
    this.applyTheme(theme);
  }

  private watchSystem(on: boolean) {
    try {
      if (on && !this.systemQuery) {
        this.systemQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.systemQuery.addEventListener('change', this.onSystemChange);
      } else if (!on && this.systemQuery) {
        this.systemQuery.removeEventListener('change', this.onSystemChange);
        this.systemQuery = null;
      }
    } catch (e) {
      // No matchMedia: the theme stays as it was resolved.
    }
  }

  private static systemTheme(): ThemeId {
    try {
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? ThemeId.DARK : ThemeId.LIGHT;
    } catch (e) {
      return ThemeId.LIGHT;
    }
  }

  /**
   * Writes the colour theme on body[data-app-theme]; the stylesheet's theme
   * blocks (src/styles/themes) match it together with the mode's class.
   */
  private applyAppTheme(id: string) {
    this.currentAppTheme = id;
    this.renderer.setAttribute(document.body, 'data-app-theme', id);
  }

  /** Swaps the body class. No persistence, no analytics. */
  private applyTheme(theme: ThemeId) {
    const previousColorTheme = theme === ThemeId.DARK ? 'light-theme' : 'dark-theme';
    const newClass = theme === ThemeId.DARK ? 'dark-theme' : 'light-theme';
    this.renderer.removeClass(document.body, previousColorTheme);
    this.renderer.addClass(document.body, newClass);
  }

  /** Reads `?appTheme=` from the URL: an embed can name its colour theme. */
  private static appThemeFromUrl(): string | null {
    const match = /[?&]appTheme=([^&#]*)/.exec(window.location.href);
    return match ? decodeURIComponent(match[1]).toLowerCase() : null;
  }

  /** In a frame on another page: an embedded map or widget. */
  private static isEmbedded(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // A cross-origin parent will not even say whether it exists.
      return true;
    }
  }

  /**
   * Reads `?theme=` from the URL. `auto` resolves against the visitor's own OS
   * setting, which is what an embedded widget on someone else's site wants.
   */
  private static themeFromUrl(): ThemeId | null {
    const match = /[?&]theme=([^&#]*)/.exec(window.location.href);
    const value = match ? decodeURIComponent(match[1]).toLowerCase() : '';
    if (value === ThemeId.LIGHT || value === ThemeId.DARK) {
      return value as ThemeId;
    }
    if (value === 'auto') {
      return ThemeService.systemTheme();
    }
    return null;
  }
}
